<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { computed, reactive } from 'vue'
import { assignDiscountToUser } from '@/api/functions/users/assign-discount-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import { numberWithCommas } from '@/utils/number-with-commas'

const props = defineProps({
  modelValue: Boolean,
  allDiscounts: {
    type: Object,
  },
  userId: {
    type: String,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()
const form = reactive({
  selectedDiscountId: null,
})

const discounts = computed(() => {
  return (
    props.allDiscounts?.data?.map((d: any) => ({
      label: d.title,
      value: d.id,
      rate: d.rate,
      volumeLimit: d.volume_limit,
    })) || []
  )
})

const selectedDiscount = computed(() => {
  return discounts.value.find((d: { value: null }) => d.value === form.selectedDiscountId) || {}
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

const queryClient = useQueryClient()

const mutation = useMutationApi({
  mutationFn: () => assignDiscountToUser(props.userId, form.selectedDiscountId as unknown as number),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['user-discounts', props.userId] })
    toast.success('تخفیف با موفقیت به کاربر اختصاص یافت')
    emit('confirm')
    closeModal()
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const confirmSave = () => {
  if (!form.selectedDiscountId) return
  mutation.mutate()
}
</script>


<template>
    <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="580px"
        background="#FFF" :closable="true" title="اختصاص تخفیف به کاربر" closeDirection="left" class="update-request">
        <form @submit.prevent="confirmSave" class="flex flex-col min-h-[300px]">

            <div class="flex-1 overflow-auto">
                <div class="grid px-4 pt-6 pb-9">
                    <Select variant="checkbox" v-model="form.selectedDiscountId" label="انتخاب تخفیف"
                        :options="discounts" @update:model-value="form.selectedDiscountId = $event" />

                    <div v-if="form.selectedDiscountId" class="flex flex-col gap-3 text-[15px] font-medium">
                        <div class="flex items-center justify-between">
                            <span class="text-[#2d5396]">نوع اعتبار:</span>
                            <span class="text-[#1b325a]">یک‌بار مصرف</span>
                        </div>
                        <div class="flex items-center justify-between">
                            <span class="text-[#2d5396]">مقدار تخفیف (٪):</span>
                            <span class="font-fa text-[#1b325a]">{{ selectedDiscount.rate }}</span>
                        </div>
                        <div class="flex items-center justify-between">
                            <span class="text-[#2d5396]">سقف حجمی تخفیف:</span>
                            <span class="font-fa text-[#1b325a]">
                                {{ numberWithCommas(selectedDiscount.volumeLimit) }}</span>
                        </div>
                    </div>

                </div>
            </div>

            <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
                <Button type="submit" label="تایید تخفیف" :isAdmin="true" class="w-9/12 h-12 rounded-xl" />
                <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
                    class="w-4/12 h-12 rounded-xl border-2" />
            </div>
        </form>
    </Modal>
</template>
